/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from "react";
import style from "../styles/productDetail.module.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import ReactMarkdown from "react-markdown";
import Quantity from "../components/shared/Quantity";
import AddToCart from "../components/shared/AddToCart";
import BuyNow from "../components/shared/BuyNow";
import ProductsSlider from "../components/shared/ProductsSlider";
import { GlobalContext } from "../context/GlobalContext";
import { useParams } from "react-router-dom";
import Loader from "../components/shared/Loader";
import withHelmet from "../seo/withHelmet";
import { singleProductHelmetProps } from "../seo/helmetProps";
import { Helmet } from "react-helmet";
import { trackViewItem } from "../utils/tracking";
import { imageUrl } from "../baseApi";
import { lineTotal, unitPrice } from "../utils/pricing";

const ProductDetail = () => {
  const { slug } = useParams();

  const { product, getProduct, loading, getRelatedPorducts, relatedProducts } =
    useContext(GlobalContext);
  const [quantity, setQuantity] = useState(1);
  const galleryRef = useRef(null);
  const [schemaData, setSchemaData] = useState({});
  const addQuantityField = (quantity) => {
    setQuantity(quantity);
  };

  useEffect(() => {
    getProduct(slug);
    setQuantity(1); // Reset quantity when product changes
  }, [slug]);

  useEffect(() => {
    if (product) {
      getRelatedPorducts(product?.id, product?.subcategory?.documentId);

      // Track product view
      trackViewItem(product);

      setSchemaData({
        "@context": "https://schema.org/",
        "@type": "Product",
        name: product.Title,
        image: "https://cms.onbeautyaesthetics.com" + product.photos[0].url,
        sku: product.documentId,
        description: product.Description,
        url: `https://www.onbeautyaesthetics.com/shop/${slug}`,
        brand: {
          "@type": "Brand",
          name: "Holiday Depilatori",
        },
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: `${product?.price.toFixed(2)}`,
          availability: product?.notInStock
            ? "https://schema.org/OutOfStock"
            : "https://schema.org/InStock",
          url: `https://www.onbeautyaesthetics.com/shop/${slug}`,
        },
      });
    }
  }, [product, slug]);
  const singleProductHelmetProps = {
    meta: [
      {
        name: "keywords",
        content:
          "skincare product, premium cream, serum, lotion, body milk, massage oil, beauty product, produkt kujdesi lëkure, krem premium, serum, locion, qumësht trupi, vaj për masazh, produkt bukurie",
      },
      // Open Graph and Twitter Cards will be added dynamically in the component
    ],
    link: [
      {
        rel: "canonical",
        href: `https://www.onbeautyaesthetics.com/shop/${slug}`,
      },
    ],
  };

  return (
    <>
      {product === undefined || loading ? (
        <Loader />
      ) : (
        <>
          <Helmet>
            {product.Title && <title>{product.Title}</title>}

            {singleProductHelmetProps.meta.map((metaProps, index) => (
              <meta key={`meta-${index}`} {...metaProps} />
            ))}
            <meta
              key={`meta-description`}
              name="description"
              content={product?.Description}
            />

            {/* Open Graph meta tags for product */}
            <meta
              property="og:title"
              content={product.Title || "Premium Skincare Product"}
            />
            <meta
              property="og:description"
              content={
                product?.Description ||
                "Premium skincare product from On Beauty Aesthetics"
              }
            />
            <meta property="og:type" content="product" />
            <meta
              property="og:url"
              content={`https://www.onbeautyaesthetics.com/shop/${slug}`}
            />
            {product?.photos?.[0] && (
              <meta
                property="og:image"
                content={`https://cms.onbeautyaesthetics.com${product.photos[0].url}`}
              />
            )}
            <meta property="og:site_name" content="On Beauty Aesthetics" />

            {/* Twitter Card meta tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta
              name="twitter:title"
              content={product.Title || "Premium Skincare Product"}
            />
            <meta
              name="twitter:description"
              content={
                product?.Description ||
                "Premium skincare product from On Beauty Aesthetics"
              }
            />
            {product?.photos?.[0] && (
              <meta
                name="twitter:image"
                content={`https://cms.onbeautyaesthetics.com${product.photos[0].url}`}
              />
            )}

            {singleProductHelmetProps.link.map((linkProps, index) => (
              <link key={`link-${index}`} {...linkProps} />
            ))}
          </Helmet>
          <div className={style.wrapper}>
            <div className={style.innerContainer}>
              <div className={style.product}>
                <Swiper
                  direction={"horizontal"}
                  slidesPerView={1}
                  navigation={true}
                  spaceBetween={30}
                  loop
                  autoHeight
                  pagination={{
                    clickable: true,
                  }}
                  modules={[Navigation]}
                  className={style.productsImages}
                  onSwiper={(swiper) => {
                    galleryRef.current = swiper;
                  }}
                >
                  {product?.photos.map((photo) => {
                    return (
                      <SwiperSlide className={style.photos} key={photo.url}>
                        <img
                          src={`${imageUrl}${photo.url}`}
                          alt={product?.Title || ""}
                          onLoad={() => galleryRef.current?.updateAutoHeight()}
                        />
                      </SwiperSlide>
                    );
                  })}
                </Swiper>

                <div className={style.productData}>
                  <h1>{product?.Title}</h1>
                  <div className={style.price}>
                    {product?.discount > 0 ? (
                      <p className={style.old}>{product.price.toFixed(2)}€</p>
                    ) : null}
                    <p className={style.current}>
                      {unitPrice(product).toFixed(2)}€
                    </p>
                  </div>
                  {product?.notInStock ? (
                    <p className={style.soldOut}>E shitur</p>
                  ) : (
                    <>
                      <Quantity
                        onQuantityChange={addQuantityField}
                        itemId={product?.id}
                        isCart={false}
                      />

                      <div className={style.total}>
                        <h4>Total:</h4>{" "}
                        <h4>{lineTotal(product, quantity).toFixed(2)}€</h4>
                      </div>
                      <div className={style.buttons}>
                        <AddToCart product={product} quantity={quantity} />
                        <BuyNow product={product} quantity={quantity} />
                      </div>
                    </>
                  )}
                  <div className={style.description}>
                    <ReactMarkdown className={style.markDown}>
                      {product?.Description}
                    </ReactMarkdown>
                    <p className={style.origin}>
                      Origjina: <strong>{product?.madeIn}</strong>
                    </p>
                  </div>
                </div>
              </div>
              <div className={style.relatedPorducts}>
                {relatedProducts === undefined ||
                relatedProducts?.length === 0 ||
                loading ? (
                  <></>
                ) : (
                  <>
                    <h1 className={style.title}>Produkte të ngjashme</h1>
                    <ProductsSlider relatedProducts={relatedProducts} />
                  </>
                )}
              </div>
            </div>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default withHelmet(ProductDetail, singleProductHelmetProps);
